import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { PageShell, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import type { AdminRole } from '@/lib/api-admin';
import {
  NOTIFICATION_INTEGRATION_WRITE_ROLES,
  NOTIFICATION_TEMPLATE_READ_ROLES,
} from '../lib/notificationActionRbac';
import { PromotionalTab } from '../tabs/PromotionalTab';
import { InformationalTab } from '../tabs/InformationalTab';
import { AutoNotificationTab } from '../tabs/AutoNotificationTab';
import { TemplateTab } from '../tabs/TemplateTab';
import { SendHistoryTab } from '../tabs/SendHistoryTab';
import { IntegrationTab } from '../tabs/IntegrationTab';

/**
 * 메시지(알림톡) — 단일 5탭 페이지. 참고 소스 MessagePage 구조 이식(단일 몰: tenant 제거).
 *
 * 탭 키는 스펙 정본(참고 MessagePage 동일): promotional / auto-notification / template /
 * history / sms-integration. 탭 상태는 `?tab=` 쿼리로 보존(딥링크/뒤로가기 친화). 구 분리
 * 라우트(/notifications/triggers|templates|send/*)는 router 에서 해당 탭으로 redirect.
 *
 * 기본 탭: 참고 소스 기본값(promotional)을 따르되, RBAC 상 접근 가능한 첫 탭으로 폴백
 * (예: 운영 write 권한이 없는 조회 전용 역할은 template 로 시작).
 */
type TabKey = 'promotional' | 'informational' | 'auto-notification' | 'template' | 'history' | 'sms-integration';

const TAB_ORDER: ReadonlyArray<TabKey> = [
  'promotional',
  // QA-419 — 정보성 메시지(문자·메일 직접 작성, 광고 표기·야간 제한 없음).
  'informational',
  'auto-notification',
  'template',
  'history',
  'sms-integration',
];

/** 탭 i18n 라벨 키(키에 하이픈 포함 → 매핑). */
const TAB_LABEL_KEY: Record<TabKey, string> = {
  promotional: 'tabs.promotional',
  informational: 'tabs.informational',
  'auto-notification': 'tabs.auto',
  template: 'tabs.template',
  history: 'tabs.history',
  'sms-integration': 'tabs.integration',
};

/**
 * 탭별 접근 가능 역할. 운영(광고성/자동알림/연동)은 SUPER/OPS, 조회성(템플릿/내역)은
 * CS_AGENT/AUDITOR 포함. 페이지 진입 가드(NOTIFICATION_PAGE_READ_ROLES)를 통과한 뒤,
 * 접근 불가 탭은 트리거를 숨기고 기본 탭 폴백에 사용.
 */
const TAB_ROLES: Record<TabKey, ReadonlyArray<AdminRole>> = {
  promotional: NOTIFICATION_INTEGRATION_WRITE_ROLES,
  informational: NOTIFICATION_INTEGRATION_WRITE_ROLES,
  'auto-notification': NOTIFICATION_INTEGRATION_WRITE_ROLES,
  template: NOTIFICATION_TEMPLATE_READ_ROLES,
  history: NOTIFICATION_TEMPLATE_READ_ROLES,
  'sms-integration': NOTIFICATION_INTEGRATION_WRITE_ROLES,
};

const isTabKey = (value: string | null): value is TabKey =>
  value !== null && (TAB_ORDER as ReadonlyArray<string>).includes(value);

export const NotificationMessagePage = () => {
  const { t } = useTranslation('notifications');
  const [searchParams, setSearchParams] = useSearchParams();

  // 탭별 접근 가능 여부(훅은 최상위에서 고정 순서 호출).
  const canPromotional = useHasRole(TAB_ROLES.promotional);
  const canInformational = useHasRole(TAB_ROLES.informational);
  const canAuto = useHasRole(TAB_ROLES['auto-notification']);
  const canTemplate = useHasRole(TAB_ROLES.template);
  const canHistory = useHasRole(TAB_ROLES.history);
  const canIntegration = useHasRole(TAB_ROLES['sms-integration']);

  const accessible = useMemo<Record<TabKey, boolean>>(
    () => ({
      promotional: canPromotional,
      informational: canInformational,
      'auto-notification': canAuto,
      template: canTemplate,
      history: canHistory,
      'sms-integration': canIntegration,
    }),
    [canPromotional, canInformational, canAuto, canTemplate, canHistory, canIntegration],
  );

  const visibleTabs = useMemo(() => TAB_ORDER.filter((tab) => accessible[tab]), [accessible]);

  // RBAC 폴백: 접근 가능한 첫 탭. 모두 불가(이론상 불가 — 가드 통과)면 promotional.
  const defaultTab: TabKey = visibleTabs[0] ?? 'promotional';

  const rawTab = searchParams.get('tab');
  const requested: TabKey = isTabKey(rawTab) ? rawTab : defaultTab;
  // 요청 탭이 접근 불가면 기본 탭으로 보정.
  const activeTab: TabKey = accessible[requested] ? requested : defaultTab;

  const onTabChange = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  const breadcrumb = useMemo(
    () => [
      { label: t('page.group', '알림') },
      { label: t('page.breadcrumbMessage', '메시지(알림톡)') },
      { label: t(TAB_LABEL_KEY[activeTab]) },
    ],
    [t, activeTab],
  );

  return (
    <PageShell
      title={t('page.title', '메시지(알림톡)')}
      breadcrumb={breadcrumb}
      data-testid="notification-message-page"
    >
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList aria-label={t('page.title', '메시지(알림톡)')}>
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}>
              {t(TAB_LABEL_KEY[tab])}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* forceMount 미사용 — 활성 탭만 마운트해 불필요 쿼리 방지. */}
        <TabsContent value="promotional">
          {activeTab === 'promotional' ? <PromotionalTab /> : null}
        </TabsContent>
        <TabsContent value="informational">
          {activeTab === 'informational' ? <InformationalTab /> : null}
        </TabsContent>
        <TabsContent value="auto-notification">
          {activeTab === 'auto-notification' ? <AutoNotificationTab /> : null}
        </TabsContent>
        <TabsContent value="template">
          {activeTab === 'template' ? <TemplateTab /> : null}
        </TabsContent>
        <TabsContent value="history">
          {activeTab === 'history' ? <SendHistoryTab /> : null}
        </TabsContent>
        <TabsContent value="sms-integration">
          {activeTab === 'sms-integration' ? <IntegrationTab /> : null}
        </TabsContent>
      </Tabs>
    </PageShell>
  );
};
